import { Link } from "react-router-dom";

import type { DescribedError } from "@/lib/github-errors";

// A crisp, branded error card: icon + title + one-line explanation + optional actionable hint and
// a call-to-action (e.g. "Reconnect in Settings"). Never shows raw server/GitHub strings unless
// they're already customer-safe. Render `null` when there's no error.
export default function ErrorNotice({
  error,
  onRetry,
  className = "",
}: {
  error: DescribedError | null;
  onRetry?: () => void;
  className?: string;
}) {
  if (!error) return null;
  const warn = error.tone === "warning";
  const ring = warn
    ? "border-amber-500/30 bg-amber-500/[0.08]"
    : "border-red-500/30 bg-red-500/[0.08]";
  const accent = warn ? "text-amber-400" : "text-red-400";

  return (
    <div className={`rounded-xl border ${ring} px-4 py-3 ${className}`} role="alert">
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 shrink-0 ${accent}`} aria-hidden>
          {warn ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <path d="M12 9v4M12 17h.01" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${accent}`}>{error.title}</p>
          <p className="mt-0.5 text-sm text-text/75">{error.detail}</p>
          {error.hint && <p className="mt-1 text-xs leading-relaxed text-text/50">{error.hint}</p>}
          {(error.action || onRetry) && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {error.action && (
                <Link
                  to={error.action.href}
                  className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                    warn
                      ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                      : "border-red-500/40 text-red-300 hover:bg-red-500/10"
                  }`}
                >
                  {error.action.label}
                </Link>
              )}
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="inline-flex items-center rounded-lg border border-white/15 px-3 py-1.5 text-xs font-medium text-text/70 transition hover:border-white/30 hover:text-text"
                >
                  Try again
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
